import {
  Badge,
  Box,
  Card,
  DataList,
  Flex,
  Grid,
  Heading,
  Link,
  Skeleton,
} from '@radix-ui/themes';
import { CommonBackArrow } from 'components/common/buttons/back-arrow';
import { ErrorBoundary } from 'components/common/error-boundary';
import FloatingCopyButton from 'components/common/form/floating-copy-button';
import { JsonViewer } from 'components/common/json/json-viewer';
import { CommonTabs } from 'components/common/tabs';
import { RequestChangeButton } from 'components/sections/admin-portal/error-types/request-change-button';
import { formatDate } from 'components/sections/admin-portal/machines/dialogs/settings/machine-errors/common';
import { ErrorCallout } from 'components/sections/admin-portal/machines/dialogs/settings/machine-errors/machine-error-callout';
import { useMachineErrorsDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineErrorsDialogState';
import { DEFAULT_ERROR_LEVEL } from 'lib_ts/enums/errors.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IError } from 'lib_ts/interfaces/common/i-error';
import {
  IErrorType,
  IErrorTypeWithLevel,
} from 'lib_ts/interfaces/common/i-error-type';
import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';

interface IErrorDetailsProps {
  error: IError;
  errorType?: IErrorType;
  onBack: () => void;
  loading?: boolean;
}

enum ErrorDetailsTabKey {
  Exception = 'Exception',
  Traceback = 'Traceback',
  ErrorType = 'Error Type',
  JSON = 'JSON',
}

export const ErrorDetails = (props: IErrorDetailsProps) => {
  const { error, errorType, onBack } = props;
  const { _created, errorID, exception, traceback } = error;
  const [activeTab, setActiveTab] = useState<ErrorDetailsTabKey>(
    ErrorDetailsTabKey.Exception
  );

  const { viewSenderErrors } = useMachineErrorsDialogState();

  const errorTypeWithLevel = useMemo(() => {
    if (!errorType) {
      return undefined;
    }

    const o: IErrorTypeWithLevel = {
      ...errorType,
      level: error.levelOverride ?? DEFAULT_ERROR_LEVEL,
    };

    return o;
  }, [error, errorType]);

  const safeDevices = useMemo(() => {
    if (!error.device) {
      // no device
      return ['N/A'];
    }

    if (error.device.length === 0) {
      // empty array
      return ['N/A'];
    }

    // normal case
    if (Array.isArray(error.device)) {
      return error.device;
    }

    // fw incorrectly provided this as a string instead of string[]
    return [error.device];
  }, [error.device]);

  return (
    <ErrorBoundary identifier="ErrorDetails">
      <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
        <Flex gap={RADIX.FLEX.GAP.SM} align="center">
          <CommonBackArrow onClick={onBack} />

          <Heading size="4">{errorID}</Heading>

          <Badge color={RADIX.COLOR.NEUTRAL}>{formatDate(_created)}</Badge>

          <Box flexGrow="1">{/* placeholder */}</Box>

          <RequestChangeButton />
        </Flex>
        <Card>
          <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
            <Grid columns="2" gap={RADIX.FLEX.GAP.LG}>
              {/* CS Message */}
              <DataList.Root orientation="vertical">
                <DataList.Item>
                  <DataList.Label color={RADIX.COLOR.SECONDARY}>
                    CS Message
                  </DataList.Label>
                  <DataList.Value>
                    <Box>
                      <ReactMarkdown
                        children={errorType?.cs_message || 'N/A'}
                      />
                    </Box>
                  </DataList.Value>
                </DataList.Item>

                {/* Resolution */}
                <DataList.Item>
                  <DataList.Label color={RADIX.COLOR.SECONDARY}>
                    Resolution
                  </DataList.Label>
                  <DataList.Value>
                    {errorType?.resolution || 'N/A'}
                  </DataList.Value>
                </DataList.Item>

                {/* Type Preview */}
                <DataList.Item>
                  <DataList.Label color={RADIX.COLOR.SECONDARY}>
                    Type Preview
                  </DataList.Label>
                  <DataList.Value>
                    <ErrorCallout
                      type={errorTypeWithLevel}
                      fallbackError={(error as any).message}
                    />
                  </DataList.Value>
                </DataList.Item>
              </DataList.Root>

              <DataList.Root orientation="vertical">
                {/* Dev Message */}
                <DataList.Item>
                  <DataList.Label color={RADIX.COLOR.SECONDARY}>
                    Dev Message
                  </DataList.Label>
                  <DataList.Value>
                    <Box>
                      <ReactMarkdown
                        children={errorType?.dev_message || 'N/A'}
                      />
                    </Box>
                  </DataList.Value>
                </DataList.Item>

                {/* Patch */}
                <DataList.Item>
                  <DataList.Label color={RADIX.COLOR.SECONDARY}>
                    Patch
                  </DataList.Label>
                  <DataList.Value>{errorType?.patch || 'N/A'}</DataList.Value>
                </DataList.Item>

                <DataList.Item>
                  <DataList.Label color={RADIX.COLOR.SECONDARY}>
                    Sender
                  </DataList.Label>
                  <DataList.Value>
                    {error.sender ? (
                      <Link
                        onClick={() => {
                          // unselects the row in the table
                          props.onBack();

                          // filters table of errors to match this sender
                          viewSenderErrors(error.sender || '');
                        }}
                        className="cursor-pointer font-mono"
                      >
                        {error.sender}
                      </Link>
                    ) : (
                      'N/A'
                    )}
                  </DataList.Value>
                </DataList.Item>

                <DataList.Item>
                  <DataList.Label color={RADIX.COLOR.SECONDARY}>
                    Owner
                  </DataList.Label>
                  <DataList.Value>
                    {errorType?.department || 'N/A'}
                  </DataList.Value>
                </DataList.Item>

                <DataList.Item>
                  <DataList.Label color={RADIX.COLOR.SECONDARY}>
                    {safeDevices.length === 1 ? 'Device' : 'Devices'}
                  </DataList.Label>
                  <DataList.Value>{safeDevices.join(', ')}</DataList.Value>
                </DataList.Item>
              </DataList.Root>
            </Grid>
          </Flex>
        </Card>
        <Flex direction="column">
          <CommonTabs
            tabs={[
              {
                label: ErrorDetailsTabKey.Exception,
                value: ErrorDetailsTabKey.Exception,
                content: (
                  <Skeleton loading={props.loading}>
                    <Box position="relative">
                      <Card>
                        <pre>{exception}</pre>
                      </Card>
                      {exception && (
                        <FloatingCopyButton
                          value={exception}
                          label={ErrorDetailsTabKey.Exception}
                        />
                      )}
                    </Box>
                  </Skeleton>
                ),
              },
              {
                label: ErrorDetailsTabKey.Traceback,
                value: ErrorDetailsTabKey.Traceback,
                content: (
                  <Box position="relative">
                    <Card>
                      <pre>{traceback}</pre>
                    </Card>
                    {traceback && (
                      <FloatingCopyButton
                        value={traceback}
                        label={ErrorDetailsTabKey.Traceback}
                      />
                    )}
                  </Box>
                ),
              },
              {
                label: ErrorDetailsTabKey.ErrorType,
                value: ErrorDetailsTabKey.ErrorType,
                content: (
                  <Box position="relative">
                    <Card>
                      <JsonViewer value={errorType} />
                    </Card>
                    {errorType && (
                      <FloatingCopyButton
                        value={JSON.stringify(errorType, null, 2)}
                        label={ErrorDetailsTabKey.ErrorType}
                      />
                    )}
                  </Box>
                ),
              },
              {
                label: ErrorDetailsTabKey.JSON,
                value: ErrorDetailsTabKey.JSON,
                content: (
                  <Box position="relative">
                    <Card>
                      <JsonViewer value={error} />
                    </Card>
                    {error && (
                      <FloatingCopyButton
                        value={JSON.stringify(error, null, 2)}
                        label={ErrorDetailsTabKey.JSON}
                      />
                    )}
                  </Box>
                ),
              },
            ]}
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as ErrorDetailsTabKey)}
            gap="1"
          />
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};
